import { LayerKindName } from "@/layers/utility/createLayerObject";
import { t } from "@lingui/core/macro";
import type { WmsLayer, WmsLayerCreate } from "@mm/metx-workbench.meteomatics.com";
import {
  type BinaryAnomalyDefinition,
  DEFAULT_BINARY_ANOMALY_DEFINITION,
  TEMPERATURE_ANOMALY_DEFINITION,
  getBinaryAnomalyDefinitionByPrimaryParameter,
} from "./BinaryAnomalyDefinitions";

export const BINARY_ANOMALY_POC_PRODUCT_ID = "temperature-anomaly-poc" as const;
export const TEMPERATURE_ANOMALY_POC_PRODUCT_ID = BINARY_ANOMALY_POC_PRODUCT_ID;

type BinaryAnomalyCompatibleLayer = Pick<
  WmsLayer,
  "kind" | "experimental" | "parameter_unit" | "calibrated" | "vertical_interpolation" | "ens_select" | "show_init_time"
>;

function hasBinaryAnomalyCompatibilityTuple(layer: unknown): layer is BinaryAnomalyCompatibleLayer {
  if (!layer || typeof layer !== "object") {
    return false;
  }
  const candidate = layer as BinaryAnomalyCompatibleLayer;
  return (
    candidate.kind === LayerKindName.WmsLayerDescription &&
    candidate.experimental === true &&
    candidate.calibrated === false &&
    candidate.vertical_interpolation === "none" &&
    (candidate.ens_select == null || candidate.ens_select === "") &&
    candidate.show_init_time === false
  );
}

export function getBinaryAnomalyDefinition(layer: unknown): BinaryAnomalyDefinition | undefined {
  if (!hasBinaryAnomalyCompatibilityTuple(layer)) {
    return undefined;
  }
  return getBinaryAnomalyDefinitionByPrimaryParameter(layer.parameter_unit);
}

export function isBinaryAnomalyLayer(layer: unknown): boolean {
  return getBinaryAnomalyDefinition(layer) !== undefined;
}

export function getBinaryAnomalyRendererKind(
  layer: unknown,
  projectionName: string | undefined,
): "binary_anomaly" | "unsupported_anomaly" | undefined {
  if (!isBinaryAnomalyLayer(layer)) {
    return undefined;
  }
  return projectionName === "mercator" ? "binary_anomaly" : "unsupported_anomaly";
}

export function createBinaryAnomalyLayerRecord(
  definition: BinaryAnomalyDefinition,
  model: string,
  index: number,
): WmsLayerCreate {
  return {
    kind: LayerKindName.WmsLayerDescription,
    label: definition.layerLabel,
    experimental: true,
    model,
    parameter_unit: definition.primaryParameter,
    color_map: definition.style.id,
    calibrated: false,
    vertical_interpolation: "none",
    ens_select: null,
    show_init_time: false,
    legend_visible: true,
    custom_options: {},
    opacity: 1,
    show: true,
    index,
  };
}

export function getBinaryAnomalyCommonModels(
  definition: BinaryAnomalyDefinition,
  matchParameterNameExactly: (parameter: string) => Array<{ parameter: { available_models?: string[] } }>,
): string[] {
  const primaryModels = matchParameterNameExactly(definition.primaryParameter)[0]?.parameter.available_models ?? [];
  const referenceModels = matchParameterNameExactly(definition.referenceParameter)[0]?.parameter.available_models ?? [];
  const referenceModelSet = new Set(referenceModels);
  const commonModels = [...new Set(primaryModels)].filter((model) => referenceModelSet.has(model)).sort();
  return commonModels.includes("mix") ? ["mix", ...commonModels.filter((model) => model !== "mix")] : commonModels;
}

export function getBinaryAnomalyFormulaLabel(definition: BinaryAnomalyDefinition): string {
  switch (definition.id) {
    case TEMPERATURE_ANOMALY_DEFINITION.id:
      return t`2 m temperature - 10-year mean`;
    default:
      return t`10 m wind speed - 10-year mean`;
  }
}

export function getBinaryAnomalyLayerLabel(definition: BinaryAnomalyDefinition): string {
  switch (definition.id) {
    case TEMPERATURE_ANOMALY_DEFINITION.id:
      return t`Temperature anomaly`;
    default:
      return t`Wind speed anomaly`;
  }
}

export function getBinaryAnomalyLegendLabel(definition: BinaryAnomalyDefinition): string {
  switch (definition.id) {
    case TEMPERATURE_ANOMALY_DEFINITION.id:
      return t`2 m temperature anomaly`;
    default:
      return t`10 m wind speed anomaly`;
  }
}

// Compatibility exports for callers outside this feature branch.
export const TEMPERATURE_ANOMALY_POC_LABEL = TEMPERATURE_ANOMALY_DEFINITION.layerLabel;
export const TEMPERATURE_ANOMALY_POC_FORMULA_LABEL = TEMPERATURE_ANOMALY_DEFINITION.formulaLabel;
export const isTemperatureAnomalyPocLayer = isBinaryAnomalyLayer;
export const getTemperatureAnomalyPocRendererKind = getBinaryAnomalyRendererKind;
export function createTemperatureAnomalyPocLayerRecord(model: string, index: number): WmsLayerCreate {
  return createBinaryAnomalyLayerRecord(DEFAULT_BINARY_ANOMALY_DEFINITION, model, index);
}
export function getTemperatureAnomalyCommonModels(
  matchParameterNameExactly: (parameter: string) => Array<{ parameter: { available_models?: string[] } }>,
): string[] {
  return getBinaryAnomalyCommonModels(DEFAULT_BINARY_ANOMALY_DEFINITION, matchParameterNameExactly);
}
